import React, { useEffect, useRef, useState } from "react";

import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import Typography from "@mui/material/Typography";

import HomeIcon from "@mui/icons-material/Home";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import Feature from "ol/Feature";
import Fill from "ol/style/Fill";
import Icon from "ol/style/Icon";
import Stroke from "ol/style/Stroke";
import Style from "ol/style/Style";
import { circular } from "ol/geom/Polygon";

import useOpenLayersMap from "../hooks/useOpenLayersMap";
import MapFullScreen from "../map/mapFullScreen";
import MapInfoCard from "../map/mapInfoCard";

import { Colors, convertHexColorToRgbaColor, GetSource } from "../map/mapUtil";
import { getHimsLabelStyleFromComponent, getSensorStateColor } from "./hmsUtil";

import { useUserAuth } from "../contexts/authContext";

import { useLocalStorage } from "../hooks/useLocalStorage";
import useFullscreenElement from "../hooks/useFullscreenElement";

export default function HMSMapComponent() {
    const MAP_ID = "hms-map-component";
    const mapContainer = useFullscreenElement();

    const map = useOpenLayersMap(MAP_ID);
    const mapWrapperRef = useRef(null);

    const [sensorFilter, setSensorFilter] = useLocalStorage("componentMapSensorFilter", "All");
    const [showHimsSensorLabels, setShowHimsSensorLabels] = useLocalStorage("showHimsSensorLabels", true);

    const [components, setComponents] = useState([]);

    const { userMapSettings, socket } = useUserAuth();

    useEffect(() => {
        socket.on("himsComponentUpdate", (components) => {
            setComponents(components);
        });
        socket.emit("getHimsComponents", socket.id);

        return () => {
            socket.off("himsComponentUpdate");
        };
    }, [socket]);

    useEffect(() => {
        if (!map) {
            return;
        }

        const vectorLayer = new VectorLayer({
            source: new VectorSource(),
            properties: { name: "hms_components", selectable: true },
            zIndex: 5
        });
        map.addLayer(vectorLayer);
        handleHomeButtonClick();
    }, [map]);

    useEffect(() => {
        if (!map || !GetSource(map, "hms_components") || !components || components.length === 0) {
            return;
        }

        const hmsSource = GetSource(map, "hms_components");
        hmsSource.clear();

        for (const component of components) {
            if (sensorFilter === "ADS-B" && component.sensor_type !== "ADS_B") {
                continue;
            } else if (sensorFilter === "Radar" && component.sensor_type !== "GROUND_BASED_RADAR") {
                continue;
            }

            const center = [component.lon, component.lat];
            const radius = component.radius;
            const geometry = circular(center, radius, 128);

            const sourceType = component.sensor_type === "ADS_B" ? "adsb" : "radar";
            const src = `../static/2d-models/hms/${sourceType}.svg`;

            const color = getSensorStateColor(component.sensor_state);
            const style = [
                new Style({
                    fill: new Fill({
                        color: convertHexColorToRgbaColor(color, 0.1)
                    }),
                    stroke: new Stroke({
                        color: convertHexColorToRgbaColor(color, 1),
                        width: 3
                    })
                }),
                new Style({
                    image: new Icon({
                        src,
                        scale: 0.6,
                        color: Colors.blue3
                    }),
                    geometry: (feature) => {
                        const geometry = feature.getGeometry();
                        if (geometry.getType() === "Polygon") {
                            return geometry.getInteriorPoint();
                        }
                    }
                })
            ];
            if (showHimsSensorLabels === true) {
                const labelStyle = getHimsLabelStyleFromComponent(component);
                style.push(labelStyle);
            }
            const feature = new Feature(geometry);
            feature.set("name", component.name);
            feature.set("hms_component", component);
            feature.setStyle(style);
            hmsSource.addFeature(feature);
        }
    }, [map, components, sensorFilter, showHimsSensorLabels]);

    const handleHomeButtonClick = () => {
        if (map && map.getView()) {
            map.getView().setCenter([userMapSettings.longitude, userMapSettings.latitude]);
            map.getView().setZoom(8);
        }
    };
    const handleFullScreenButtonClick = () => {
        try {
            if (mapContainer) {
                document.exitFullscreen();
            } else {
                mapWrapperRef.current.requestFullscreen();
            }
        } catch (err) {
            console.error(err);
        }
    };
    return (
        <Box ref={mapWrapperRef} sx={{ height: "100%", width: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>
            <Box sx={{ height: "100%", width: "100%", opacity: Array.isArray(components) ? 1 : 0.5 }}>
                <MapFullScreen map_id={MAP_ID} map={map}>
                    <Box sx={{ position: "absolute", zIndex: 1, right: "4px", top: "10px", display: "flex", flexDirection: "row", gap: "8px" }}>
                        <Paper
                            sx={{
                                display: "flex",
                                flexDirection: "row",
                                justifyContent: "center",
                                alignItems: "center",
                                width: "215px",
                                height: "38px",
                                px: "5px",
                                pl: "10px",
                                gap: "8px"
                            }}
                        >
                            <Typography variant="body1" color="textSecondary" sx={{ width: "100%" }}>
                                Sensor Filter:
                            </Typography>
                            <FormControl size="small" sx={{ width: "100%" }}>
                                <Select
                                    value={sensorFilter}
                                    onChange={(e) => setSensorFilter(e.target.value)}
                                    sx={{ maxHeight: "30px" }}
                                    MenuProps={{ container: mapContainer }}
                                >
                                    <MenuItem value="All">All</MenuItem>
                                    <MenuItem value="ADS-B">ADS-B</MenuItem>
                                    <MenuItem value="Radar">Radar</MenuItem>
                                </Select>
                            </FormControl>
                        </Paper>
                        <button
                            title="Toggle Labels"
                            type="button"
                            onClick={() => setShowHimsSensorLabels((prev) => !prev)}
                            className="control-button-dark"
                            style={{ width: "38px", height: "38px" }}
                        >
                            {showHimsSensorLabels ? <VisibilityIcon /> : <VisibilityOffIcon />}
                        </button>
                        <button
                            title="View Home"
                            type="button"
                            onClick={handleHomeButtonClick}
                            className="control-button-dark"
                            style={{ width: "38px", height: "38px" }}
                        >
                            <HomeIcon />
                        </button>
                        <button
                            title={mapContainer === null ? "Enter Fullscreen" : "Exit Fullscreen"}
                            type="button"
                            onClick={handleFullScreenButtonClick}
                            className="control-button-dark"
                            style={{ width: "38px", height: "38px" }}
                        >
                            {mapContainer === null ? <OpenInFullIcon fontSize="small" /> : <CloseIcon fontSize="small" />}
                        </button>
                    </Box>
                    <MapInfoCard map={map} mapContainer={mapContainer} />
                </MapFullScreen>
            </Box>
            {!Array.isArray(components) && <CircularProgress sx={{ position: "absolute" }} />}
        </Box>
    );
}
